import React, { useState } from 'react'
import styled from 'styled-components'
import { useProfileState, useProfileDispatch } from '../../profile.context'
import { IconMinus, IconPlus, Typography } from '../../../Common'
import Collapse from '@kunukn/react-collapse'

export const NavigationSkeleton = ({
  url,
  header,
  children,
}: NavigationSkeletonProps) => {
  const { profileTab } = useProfileState()
  const dispatch = useProfileDispatch()

  const actions = {}

  const isAction = Boolean(actions[url])

  const [collapseState, setCollapseState] = useState<
    'collapsed' | 'collapsing' | 'expanded' | 'expanding' | undefined
  >('collapsed')

  const isActive = profileTab === url

  const handleClick = () => {
    if (isAction) {
      actions[url]()
      return
    }

    if (isActive) {
      dispatch({ type: 'reset' })
      return
    }

    dispatch({ type: 'change', payload: url })
  }

  const onChange = ({ state }: { state: string }) => {
    setCollapseState(state as any)
  }

  const render = (state: string, isActive: boolean, children: any) => {
    if (isActive || collapseState !== 'collapsed') {
      return (
        <ChildrenContainer isCollapsing={state === 'collapsing'}>
          {children}
        </ChildrenContainer>
      )
    }
  }

  return (
    <Container isActive={isActive}>
      <Toggle onClick={handleClick}>
        <Header>{header}</Header>
        {!isAction && (
          <IconWrapper>{isActive ? <IconMinus /> : <IconPlus />}</IconWrapper>
        )}
      </Toggle>
      <Collapse
        isOpen={isActive}
        transition="height 0.6s cubic-bezier(.4, 0, .2, 1)"
        onChange={onChange}
        render={(state) => render(state, isActive, children)}
        addState={true}
      ></Collapse>
    </Container>
  )
}

export type NavigationSkeletonProps = {
  url: string
  header: string
  children: any
}

const IconWrapper = styled.div`
  & svg {
    width: 1.2rem;
  }
`
interface ContainerProps {
  isActive?: boolean
}

const ChildrenContainer = styled.div.attrs({
  className: 'px-4 mx-3 pt-4 pb-5',
})<ContainerProps & { isCollapsing?: boolean }>`
  & div {
    transition: all 0.4s ease-in-out;
    opacity: ${(props) => (props.isCollapsing ? 0 : 1)};

    ${(props) =>
      !props.isCollapsing &&
      `
        animation: opacityAnim 0.4s ease;
    `}
  }

  @keyframes opacityAnim {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const Container = styled.div.attrs({
  className: 'mx-auto mb-2',
})<ContainerProps>`
  background: ${({ theme }) => theme.palette.secondary};
  border-radius: 0.3rem;
`

const Toggle = styled.div.attrs({
  className: 'p-4',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`

const Header = styled(Typography).attrs({
  as: 'h2',
  variant: 6,
})`
  font-weight: 400;
`
